/**
 * Overlay Filtering Logic
 *
 * Functions for matching POIs to overlays based on accessibility attributes.
 */

import type { OverlayDefinition } from "./definitions";
import { OVERLAY_DEFINITIONS, getOverlayById } from "./definitions";
import type { POIAccessibilityAttributes, DietaryOption } from "./types";

/**
 * POI-like object with accessibility data in propertiesJson
 */
export interface POIWithAccessibility {
  id: string;
  categorySlug?: string;
  propertiesJson?: {
    accessibility?: POIAccessibilityAttributes;
    [key: string]: unknown;
  } | null;
}

/**
 * Check if a POI matches a single overlay based on its attributes
 * Uses OR logic: any matching attribute = match
 */
export function matchesPOI(
  poi: POIWithAccessibility,
  overlay: OverlayDefinition,
  subFilters?: string[]
): boolean {
  const attrs = poi.propertiesJson?.accessibility;
  if (!attrs) return false;

  // Special handling for dietary_options (array attribute)
  if (overlay.id === "dietary") {
    const dietaryOptions = attrs.dietary_options;
    if (!dietaryOptions || dietaryOptions.length === 0) return false;

    // If sub-filters specified, use AND logic for selected options
    if (subFilters && subFilters.length > 0) {
      return subFilters.every((sf) =>
        dietaryOptions.includes(sf as DietaryOption)
      );
    }
    // No sub-filters = any dietary option matches
    return true;
  }

  // Standard boolean attributes: OR logic
  return overlay.matchesAttributes.some((attrKey) => {
    const value = attrs[attrKey as keyof POIAccessibilityAttributes];
    return value === true;
  });
}

/**
 * Check if a POI matches any of the active overlays
 */
export function matchesAnyOverlay(
  poi: POIWithAccessibility,
  activeOverlayIds: string[],
  subFiltersMap?: Record<string, string[]>
): boolean {
  return activeOverlayIds.some((overlayId) => {
    const overlay = getOverlayById(overlayId);
    if (!overlay) return false;
    const subFilters = subFiltersMap?.[overlayId];
    return matchesPOI(poi, overlay, subFilters);
  });
}

/**
 * Get all overlay IDs that a POI matches
 */
export function getMatchingOverlays(poi: POIWithAccessibility): string[] {
  return OVERLAY_DEFINITIONS.filter((overlay) => matchesPOI(poi, overlay)).map(
    (o) => o.id
  );
}

/**
 * Get top N overlays that a POI matches (by priority order)
 */
export function getTopMatchingOverlays(
  poi: POIWithAccessibility,
  maxCount: number = 2
): OverlayDefinition[] {
  const matching = OVERLAY_DEFINITIONS.filter((overlay) =>
    matchesPOI(poi, overlay)
  );
  // Already in priority order since OVERLAY_DEFINITIONS is ordered
  return matching.slice(0, maxCount);
}

/**
 * Check if any active overlay has dimNonMatching enabled
 */
export function shouldDimNonMatching(activeOverlayIds: string[]): boolean {
  return activeOverlayIds.some((id) => {
    const overlay = getOverlayById(id);
    return overlay?.dimNonMatching === true;
  });
}

/**
 * Determine if a POI should be dimmed based on active overlays
 * POI is dimmed if:
 * 1. Any active overlay has dimNonMatching: true
 * 2. AND the POI doesn't match ANY active overlay
 */
export function shouldDimPOI(
  poi: POIWithAccessibility,
  activeOverlayIds: string[],
  subFiltersMap?: Record<string, string[]>
): boolean {
  if (!shouldDimNonMatching(activeOverlayIds)) {
    return false;
  }
  return !matchesAnyOverlay(poi, activeOverlayIds, subFiltersMap);
}

/**
 * Count POIs matching each overlay
 */
export function countPOIsPerOverlay(
  pois: POIWithAccessibility[]
): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const overlay of OVERLAY_DEFINITIONS) {
    counts[overlay.id] = pois.filter((poi) => matchesPOI(poi, overlay)).length;
  }

  return counts;
}

/**
 * Get accessibility coverage stats for a set of POIs
 */
export interface AccessibilityCoverage {
  totalPois: number;
  poisWithAnyAccessibility: number;
  coveragePercent: number;
  perOverlay: Record<string, number>;
}

export function getAccessibilityCoverage(
  pois: POIWithAccessibility[]
): AccessibilityCoverage {
  const poisWithAny = pois.filter((poi) => {
    const attrs = poi.propertiesJson?.accessibility;
    if (!attrs) return false;
    // Has at least one truthy attribute
    return Object.values(attrs).some((v) =>
      Array.isArray(v) ? v.length > 0 : v === true
    );
  });

  return {
    totalPois: pois.length,
    poisWithAnyAccessibility: poisWithAny.length,
    coveragePercent:
      pois.length > 0 ? Math.round((poisWithAny.length / pois.length) * 100) : 0,
    perOverlay: countPOIsPerOverlay(pois),
  };
}

/**
 * Extract accessibility attributes from POI for display
 * Returns only attributes that are set to true or have values
 */
export function getActiveAccessibilityAttributes(
  poi: POIWithAccessibility
): Partial<POIAccessibilityAttributes> {
  const attrs = poi.propertiesJson?.accessibility;
  if (!attrs) return {};

  const active: Partial<POIAccessibilityAttributes> = {};

  for (const [key, value] of Object.entries(attrs)) {
    if (value === true) {
      (active as Record<string, unknown>)[key] = true;
    } else if (Array.isArray(value) && value.length > 0) {
      (active as Record<string, unknown>)[key] = value;
    }
  }

  return active;
}
